/**
 * Mastermind Player Route — fullscreen Vidstack player for K2K recordings.
 *
 * Mounted at /mastermind/<uuid>. Opened in a new tab from the "Watch recording"
 * pill (RichPostContent). On load:
 *   1. Fetches a short-lived credential from the mastermind-stream edge function
 *      (Authorization-gated, RLS check on K2K access).
 *   2. Renders Vidstack <MediaPlayer> on whichever of the two the function
 *      answered with. A sitting with a verified Cloudflare Stream copy gets an
 *      HLS address carrying a signed pass; the rest get a token for the edge
 *      function's byte proxy, whose range requests authenticate via that token.
 *      Either way the R2 URL and the Cloudflare video id never reach the
 *      browser.
 *
 * The address bar shows /mastermind/<uuid> rather than the long credential URL,
 * which makes the page nicer to share back into the app and harder to copy
 * out by accident.
 *
 * RENEWAL, ADDED 15 Sep (ticket 86d40p593). This route previously held a single
 * four-hour token for a whole sitting and had no way to replace it: when it
 * expired, playback simply stopped, and a paused sitting resumed into a stall.
 * The token now carries `iat`/`exp` and `useStreamPass` — the same machinery the
 * community-recording route uses — replaces it at 80% of its life, retrying
 * with backoff and giving up on a refusal rather than hammering the endpoint.
 *
 * The four hours went on 3 Oct 2026. It had survived renewal because 77 of the
 * 80 mastermind rows carried no duration to size a shorter window against;
 * migration 620 filled every one in, and the longest sitting in the cohort is
 * 84.5 minutes. The function now reads the same VIDEO_STREAMING_TTL_SECONDS as
 * every other video surface — two hours by default — which covers the longest
 * mastermind with 35 minutes to spare before renewal is counted at all. The
 * constant in supabase/functions/mastermind-stream/index.ts carries the
 * measurements. Nothing on this page assumed the old figure: the player
 * schedules its renewal from the credential's own `iat`/`exp`, never from a
 * number written here.
 *
 * This is also the second caller of the hook, which is the point of doing it
 * here first: it proves the extraction generalises before the shared VideoPlayer
 * — which lessons, events and chat all depend on — is made to rely on it.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import {
  isHLSProvider,
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  type MediaProviderAdapter,
} from '@vidstack/react';
import { DefaultVideoLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
import Hls from 'hls.js';
import { Loader2 } from 'lucide-react';
import { BASE_HLS_CONFIG } from '@/components/media/hls-config';
import { useStreamPass } from '@/hooks/use-stream-pass';

export const Route = createFileRoute('/mastermind/$mastermindId' as any)({
  component: MastermindPlayerPage,
});

function MastermindPlayerPage() {
  const { mastermindId } = Route.useParams() as { mastermindId: string };

  const playerRef = useRef<MediaPlayerInstance>(null);
  const resumeAtRef = useRef<number | null>(null);
  const wasPlayingRef = useRef(false);
  const hasSeekRef = useRef(false);

  /**
   * Capture the viewer's place immediately before a renewed credential is
   * swapped in. A new `src` reloads every player from the start, paused —
   * losing a viewer's place two hours into a sitting is worse than the expiry
   * this exists to prevent.
   */
  const captureBeforeSwap = useCallback(() => {
    resumeAtRef.current = playerRef.current?.currentTime ?? null;
    wasPlayingRef.current = playerRef.current?.paused === false;
  }, []);

  const { source, error, renewOnError } = useStreamPass({
    resourceId: mastermindId,
    tokenUrl: (base) =>
      `${base}/functions/v1/mastermind-stream?action=token&id=${encodeURIComponent(mastermindId)}`,
    proxyUrl: (base, token) =>
      `${base}/functions/v1/mastermind-stream?action=stream&id=${encodeURIComponent(
        mastermindId,
      )}&t=${encodeURIComponent(token)}`,
    onBeforeSwap: captureBeforeSwap,
  });

  // A new source means a fresh seek is owed.
  useEffect(() => {
    hasSeekRef.current = false;
  }, [source]);

  /**
   * This route now gets HLS, not only a proxied MP4.
   *
   * `mastermind-stream?action=token` learned to answer `mode: 'stream'` on 2 Oct
   * 2026 for the sittings that have a verified Cloudflare copy — 23 of the 80 —
   * so an `.m3u8` source reaches this player for the first time. Vidstack
   * resolves hls.js by dynamic import from a CDN unless `provider.library` is
   * set, which would make playback on the surface every legacy "Watch
   * recording" pill opens depend on a third-party fetch at play time. Hand it
   * the bundled, version-matched copy instead, exactly as the recording route
   * and the shared VideoPlayer do.
   *
   * Never the cache-bust loader: against Cloudflare it forces a cache miss on
   * every segment for every viewer. Proxied MP4s never reach this branch.
   */
  const onProviderChange = useCallback((provider: MediaProviderAdapter | null) => {
    if (!provider || !isHLSProvider(provider)) return;
    provider.library = Hls;
    provider.config = { ...BASE_HLS_CONFIG };
  }, []);

  /** Put the viewer back where they were, and playing if they were playing. */
  const onCanPlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const resumeAt = resumeAtRef.current;
    if (resumeAt && resumeAt > 0 && !hasSeekRef.current) {
      player.currentTime = resumeAt;
      hasSeekRef.current = true;
      resumeAtRef.current = null;
    }
    if (wasPlayingRef.current) {
      wasPlayingRef.current = false;
      // The viewer already gestured to start this video, so autoplay policy is
      // satisfied. Swallow the rejection anyway — a refused resume should leave
      // them looking at a paused player, not an error screen.
      void player.play().catch(() => {});
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      {error ? (
        <div className="text-center text-white px-6">
          <p className="text-lg font-medium">Couldn't load recording</p>
          <p className="text-sm text-white/70 mt-1">{error}</p>
        </div>
      ) : !source ? (
        <Loader2 className="w-8 h-8 text-white/70 animate-spin" />
      ) : (
        <MediaPlayer
          ref={playerRef}
          src={source}
          // No autoPlay. The hook swaps `src` on every renewal, and Vidstack
          // plays a new source when autoPlay is set — so a sitting someone had
          // paused would start playing, with sound, at the silent 80% refresh.
          // Play state is restored from wasPlayingRef in onCanPlay instead, the
          // same way the recording route does it.
          playsInline
          className="w-full h-full"
          onProviderChange={onProviderChange}
          onCanPlay={onCanPlay}
          onError={renewOnError}
        >
          <MediaProvider />
          <DefaultVideoLayout icons={defaultLayoutIcons} />
        </MediaPlayer>
      )}
    </div>
  );
}
